(ns web3.components.autocomplete
  (:require [web3.view :as v]
            [reagent.core :as r]
            [web3.components.text :as text]
            [web3.constants :as constants]
            [web3.ui-state :as state]
            [re-frame-simple.db :as db]
            [web3.forms.validation :as validation]
            [web3.ui-state :as ui-state]
            [web3.log :as log]
            [cljs.spec.alpha :as s]
            [web3.icons.shapes :as shapes]
            [web3.view.hiccup :as hiccup]))

(def ROW_HEIGHT 41)

(db/defquery scroll-state []
  (select-keys @state/ui-globals [:window/scroll-y
                                  :window/inner-height
                                  :lightbox/scroll-y]))

(defn autocomplete-position [^js dom-node]
  (when-let [^js rect (some-> dom-node (.getBoundingClientRect))]
    (let [{:keys [window/inner-height]
           lightbox-y :lightbox/scroll-y
           window-y :window/scroll-y} (scroll-state)
          mobile? (ui-state/mobile?)
          scroll-y (or lightbox-y window-y)]
      {:top (-> (.-bottom rect)
                (+ 5)
                (cond-> mobile? (+ scroll-y)))
       :left (.-left rect)
       :width (.-offsetWidth (.-parentNode dom-node))
       :height "auto"
       :position (if mobile? "absolute" "fixed")
       :min-height 36
       :max-height (min (- inner-height
                           (.-bottom rect)
                           20)
                        (* 3.5 ROW_HEIGHT))})))

(defn autocomplete-change! [{:as component
                             {:keys [change-value! on-change on-save on-change* options]} :props} value e]
  (when (or change-value! on-change on-save)
    (.preventDefault e)
    (when change-value! (change-value! value))
    (when on-change (on-change #js {:target #js {:value value}}))
    (when on-change*
      ;; on-change* reports the entire 'option' that was selected,
      ;; whereas on-change reports only its :value key
      (on-change* (first (filter #(= value (:value %)) options))))))

(defn save! [{:as component
              {:keys [on-save]} :props} e]
  (when on-save (on-save e)))

(comment
 ;; not used anymore, the dom-node we pass around now _is_ the input
 (defn blur-first-input [dom-node]
   (some-> (.getElementsByTagName dom-node "INPUT")
           (aget 0)
           (.blur))))

(defn handle-autocomplete-keydown [{:keys [state props] :as this} options e]
  (let [{:keys [selected-i dom-node focused?]} @state
        select! #(do (.preventDefault e)
                     (swap! state assoc :selected-i %))
        blur! #(.blur dom-node)
        set-value! #(do (swap! state dissoc :selected-i)
                        (autocomplete-change! this
                                              (:value (nth options selected-i))
                                              e)
                        (save! this e))
        option-count (count options)]
    (when focused?
      (case (.-which e)
        ;; up
        38 (select! (cond (nil? selected-i) (dec option-count)
                          (= selected-i 0) (dec option-count)
                          :else (dec selected-i)))
        ;; down
        40 (select! (cond (nil? selected-i) 0
                          (> (dec option-count) selected-i) (inc selected-i)
                          :else 0))

        ;; enter or tab
        (13 9) (when (and (some? selected-i)
                          (>= selected-i 0)
                          (< selected-i (count options))) (set-value!))

        ;; escape
        27 (do (.stopPropagation e)
               (blur!))
        nil))))

(v/defview autocomplete-options
  {:component-did-update
   (fn [this]
     (let [this-node (r/dom-node this)
           i (:selected-i (:props this))
           height (.-offsetHeight this-node)
           current-start (* i ROW_HEIGHT)
           current-end (+ ROW_HEIGHT current-start)
           current-scroll (.-scrollTop this-node)]
       (when i
         (cond (< current-start current-scroll)
               (aset this-node "scrollTop" current-start)

               (> current-end (+ current-scroll height))
               (aset this-node "scrollTop" (-> (* i ROW_HEIGHT)
                                               (+ (- height ROW_HEIGHT))))))))}
  [{:keys [dom-node value options selected-i]}]
  (let [this (r/current-component)
        position (autocomplete-position dom-node)]
    [:div.bg-white.shadow-paper.text-size-17.weight-medium.cursor-pointer.overflow-y-auto
     {:style (merge position
                    {:z-index (:autocomplete constants/z-index)
                     :transition "all ease 0.1s"})}
     (doall (for [[i {option-value :value
                      :keys [label]}] (partition 2 (interleave
                                                    (range)
                                                    ;; TODO
                                                    ;; backend should not send multiple tags w/ same name.
                                                    ;; remove this distinct-by when that is fixed.
                                                    #_(utils/distinct-by
                                                       :label options)
                                                    options))
                  :let [current-value? (= option-value value)
                        selected? (= selected-i i)
                        label (or label option-value)
                        handle-click (fn [e]
                                       (.preventDefault e)
                                       (autocomplete-change! this option-value e)
                                       (save! this e)
                                       (.blur dom-node))]]
              [:div.pad-all-2_5.hover-bg-darken-1.flex.items-center.justify-between
               {:key i
                :class (str (when selected?
                              "bg-darken-2")
                            (when current-value?
                              " opacity-50"))
                :on-click handle-click
                :on-mouse-down handle-click}
               (or label option-value)
               (when current-value?
                 shapes/checkmark-thin)]))]))

(s/def ::value string?)
(s/def ::label string?)
(s/def ::option (s/keys :req-un [::value]
                         :opt-un [::label]))

(s/def ::options (s/nilable (s/coll-of ::option)))

(v/defview autocomplete
  {:component-will-receive-props
   (fn [{:keys [props state]} [_ next-props]]
     (when (and (not= (:options props) (:options next-props))
                (some? (:selected-i @state)))
       (v/swap-silently! state assoc :selected-i 0)))}
  [{:as props
    :keys [options
           value
           on-change
           change-value!
           on-change*                                       ;; like on-change but called with entire option, not only :value
           on-save

           validators

           on-focus
           on-key-down
           on-blur]}]
  ;; NOTE
  ;; autocomplete should be upgraded somehow to pass its textfield change events
  ;; independently from its selection events
  (when-not (s/valid? ::options options)
    (log/warn "Invalid options passed to autocomplete")
    (log/warn options))
  (let [{:keys [state] :as this} (r/current-component)
        {:keys [focused? dom-node selected-i]} @state]
    [:div.relative
     [text/input (v/merge-props (dissoc props :options :change-value! :on-save :on-change*)
                                {:ref #(when (and % (not dom-node))
                                         (v/swap-silently! state assoc :dom-node %))
                                 :on-focus (fn [e]
                                             (swap! state assoc :focused? true)
                                             (when on-focus (on-focus e)))
                                 :on-key-down (fn [e]
                                                (handle-autocomplete-keydown this options e)
                                                (when on-key-down
                                                  (on-key-down e)))
                                 :on-change (fn [^js e]
                                              (autocomplete-change! this (.. e -target -value) e))
                                 :helptext-pad? true
                                 :content/inline-right
                                 (when (and focused?
                                            (or (not validators)
                                                (not (validation/invalid?
                                                      (validation/validate-field
                                                       validators
                                                       value)))))
                                   [:div.text-dark-gray.hover-text-black-hover.cursor-pointer.active-text-black-hover
                                    {:on-click (partial save! this)
                                     :on-mouse-down #(do (.preventDefault %)
                                                         (save! this %))}
                                    (-> shapes/checkmark-thick
                                        (hiccup/size 18))])
                                 :on-blur (fn [e]
                                            (js/setTimeout (fn [] (swap! state assoc :focused? false))
                                                           10)
                                            (when on-blur (on-blur e)))})]
     (when (and focused?
                (seq options)
                dom-node
                #_(not (and (= (count options) 1)
                            (= (:value (first options)) value))))
       (v/portal
        [autocomplete-options {:dom-node dom-node
                               :selected-i selected-i
                               :value value
                               :change-value! change-value!
                               :on-change on-change
                               :on-change* on-change*
                               :on-save on-save
                               :options options}]))]))
